import { useState, useEffect, useCallback } from 'react'
import { getNexusClient } from '@/lib/supabase/client'
import type { UserEmailAccountRow } from '@/lib/supabase/types'

export interface EmailAccount {
  id: string
  adminUserId: string
  emailAddress: string
  displayName: string | null

  // IMAP Settings
  imapHost: string
  imapPort: number
  imapUsername: string
  imapPassword: string
  imapUseSsl: boolean

  // SMTP Settings
  smtpHost: string
  smtpPort: number
  smtpUsername: string
  smtpPassword: string
  smtpUseTls: boolean

  isActive: boolean
  lastSyncAt: string | null
  createdAt: string
  updatedAt: string
}

function mapRowToEmailAccount(row: UserEmailAccountRow): EmailAccount {
  return {
    id: row.id,
    adminUserId: row.admin_user_id,
    emailAddress: row.email_address,
    displayName: row.display_name,
    imapHost: row.imap_host,
    imapPort: row.imap_port,
    imapUsername: row.imap_username,
    imapPassword: row.imap_password,
    imapUseSsl: row.imap_use_ssl,
    smtpHost: row.smtp_host,
    smtpPort: row.smtp_port,
    smtpUsername: row.smtp_username,
    smtpPassword: row.smtp_password,
    smtpUseTls: row.smtp_use_tls,
    isActive: row.is_active,
    lastSyncAt: row.last_sync_at,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  }
}

interface UseEmailAccountsOptions {
  userId?: string
  activeOnly?: boolean
}

export function useEmailAccounts(options: UseEmailAccountsOptions = {}) {
  const { userId, activeOnly = false } = options

  const [accounts, setAccounts] = useState<EmailAccount[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const fetchAccounts = useCallback(async () => {
    setIsLoading(true)
    setError(null)

    try {
      const client = getNexusClient()
      let query = client.from('user_email_accounts').select('*')

      // Filter by user if specified
      if (userId) {
        query = query.eq('admin_user_id', userId)
      }

      // Apply active filter
      if (activeOnly) {
        query = query.eq('is_active', true)
      }

      query = query.order('created_at', { ascending: false })

      const { data, error: queryError } = await query

      if (queryError) throw queryError

      setAccounts((data || []).map(mapRowToEmailAccount))
    } catch (err: any) {
      console.error('Error fetching email accounts:', err)
      setError(err.message)
    } finally {
      setIsLoading(false)
    }
  }, [userId, activeOnly])

  useEffect(() => {
    fetchAccounts()
  }, [fetchAccounts])

  return { accounts, isLoading, error, refetch: fetchAccounts }
}

export interface CreateEmailAccountInput {
  adminUserId: string
  emailAddress: string
  displayName?: string

  // IMAP Settings
  imapHost: string
  imapPort?: number
  imapUsername: string
  imapPassword: string
  imapUseSsl?: boolean

  // SMTP Settings
  smtpHost: string
  smtpPort?: number
  smtpUsername: string
  smtpPassword: string
  smtpUseTls?: boolean
}

export interface UpdateEmailAccountInput {
  emailAddress?: string
  displayName?: string

  // IMAP Settings
  imapHost?: string
  imapPort?: number
  imapUsername?: string
  imapPassword?: string
  imapUseSsl?: boolean

  // SMTP Settings
  smtpHost?: string
  smtpPort?: number
  smtpUsername?: string
  smtpPassword?: string
  smtpUseTls?: boolean

  isActive?: boolean
}

export function useEmailAccountMutations() {
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const createAccount = async (input: CreateEmailAccountInput): Promise<EmailAccount | null> => {
    setIsLoading(true)
    setError(null)

    try {
      const client = getNexusClient()

      const { data, error: insertError } = await client
        .from('user_email_accounts')
        .insert({
          admin_user_id: input.adminUserId,
          email_address: input.emailAddress,
          display_name: input.displayName || null,
          imap_host: input.imapHost,
          imap_port: input.imapPort || 993,
          imap_username: input.imapUsername,
          imap_password: input.imapPassword,
          imap_use_ssl: input.imapUseSsl ?? true,
          smtp_host: input.smtpHost,
          smtp_port: input.smtpPort || 587,
          smtp_username: input.smtpUsername,
          smtp_password: input.smtpPassword,
          smtp_use_tls: input.smtpUseTls ?? true,
          is_active: true,
          last_sync_at: null,
        } as any)
        .select()
        .single()
        .returns<UserEmailAccountRow>()

      if (insertError) throw insertError
      return mapRowToEmailAccount(data as UserEmailAccountRow)
    } catch (err: any) {
      console.error('Error creating email account:', err)
      setError(err.message)
      return null
    } finally {
      setIsLoading(false)
    }
  }

  const updateAccount = async (accountId: string, input: UpdateEmailAccountInput): Promise<EmailAccount | null> => {
    setIsLoading(true)
    setError(null)

    try {
      const client = getNexusClient()

      const updateData: Record<string, any> = {}
      if (input.emailAddress !== undefined) updateData.email_address = input.emailAddress
      if (input.displayName !== undefined) updateData.display_name = input.displayName
      if (input.imapHost !== undefined) updateData.imap_host = input.imapHost
      if (input.imapPort !== undefined) updateData.imap_port = input.imapPort
      if (input.imapUsername !== undefined) updateData.imap_username = input.imapUsername
      if (input.imapPassword !== undefined) updateData.imap_password = input.imapPassword
      if (input.imapUseSsl !== undefined) updateData.imap_use_ssl = input.imapUseSsl
      if (input.smtpHost !== undefined) updateData.smtp_host = input.smtpHost
      if (input.smtpPort !== undefined) updateData.smtp_port = input.smtpPort
      if (input.smtpUsername !== undefined) updateData.smtp_username = input.smtpUsername
      if (input.smtpPassword !== undefined) updateData.smtp_password = input.smtpPassword
      if (input.smtpUseTls !== undefined) updateData.smtp_use_tls = input.smtpUseTls
      if (input.isActive !== undefined) updateData.is_active = input.isActive

      const { data, error: updateError } = await (client as any)
        .from('user_email_accounts')
        .update(updateData)
        .eq('id', accountId)
        .select()
        .single()

      if (updateError) throw updateError
      return mapRowToEmailAccount(data as UserEmailAccountRow)
    } catch (err: any) {
      console.error('Error updating email account:', err)
      setError(err.message)
      return null
    } finally {
      setIsLoading(false)
    }
  }

  const deleteAccount = async (accountId: string): Promise<boolean> => {
    setIsLoading(true)
    setError(null)

    try {
      const client = getNexusClient()

      const { error: deleteError } = await client
        .from('user_email_accounts')
        .delete()
        .eq('id', accountId)

      if (deleteError) throw deleteError
      return true
    } catch (err: any) {
      console.error('Error deleting email account:', err)
      setError(err.message)
      return false
    } finally {
      setIsLoading(false)
    }
  }

  const toggleAccountActive = async (accountId: string, isActive: boolean): Promise<boolean> => {
    const result = await updateAccount(accountId, { isActive })
    return result !== null
  }

  const testConnection = async (input: CreateEmailAccountInput): Promise<{ success: boolean; message: string }> => {
    // This would need a backend API to test IMAP/SMTP connections
    // For now, return a placeholder
    return {
      success: true,
      message: 'Connection test not yet implemented. Credentials saved.',
    }
  }

  return {
    createAccount,
    updateAccount,
    deleteAccount,
    toggleAccountActive,
    testConnection,
    isLoading,
    error,
  }
}
